import { ArrowRight, Package, Search } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Chip, EmptyState, Input, PageHeader, Toolbar, useToast } from '@/components/ui';
import { ObjectLink } from '@/components/layout/workspace/ObjectLink';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import { matchesQuery } from '@/lib/searchText';
import { at, kindLabel, MARKETPLACE, MODULE_ICONS, SAMPLE_ALERT, withServices, type Module } from '../model';
import { usePrefs } from '../prefs';
import { Body, Page, Panel } from './common';
import { ObjectNotFound } from './StateScreens';

/*
  Marketplace — страница платформы (уровень 2). Карточка модуля открывается вкладкой (R37),
  «Открыть» у приложения уводит в него: второй уровень уезжает под главное меню, раскрывается
  третий. Поиск по каталогу понимает транслит и не ту раскладку (R40). Подключение — сразу, с
  отменой в тосте и Ctrl+Z (R39); вернулся на «Мои сервисы» — там выходит новая пластина.
*/

/** before — что было подключено, когда раздел открыли. */
function viewFilter(view: string, before: readonly string[]): (m: Module) => boolean {
  // Подключённое за этот заход остаётся на месте с «Настроить»: карточки не съезжают, и второй щелчок
  // двойного нажатия не подключает соседнюю.
  if (view === 'recommended') return (m) => !before.includes(m.id);
  if (view === 'all') return () => true;
  return (m) => m.category === view;
}

/** Каталог глазами человека и сбой образца. */
function useCatalog() {
  const prefs = usePrefs();
  return {
    catalog: withServices(prefs.services, prefs.servicesOff),
    alertId: prefs.serviceAlert ? SAMPLE_ALERT.id : null,
  };
}

/** Подключить модуль: сразу, с «Отменить» в тосте; отмена убирает его из актуального списка. */
function useConnect() {
  const { set } = usePrefs();
  const { toast } = useToast();
  return (m: Module) => {
    set((p) => ({ services: p.services.includes(m.id) ? p.services : [...p.services, m.id] }));
    toast({
      title: `Модуль «${m.name}» подключён`,
      tone: 'success',
      action: {
        label: 'Отменить',
        undo: true,
        onClick: () =>
          set((p) => ({
            services: p.services.filter((id) => id !== m.id),
            servicesOff: p.servicesOff.filter((id) => id !== m.id),
          })),
      },
    });
  };
}

export function MarketplaceScreen() {
  const view = useParams()['*'] || 'all';
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<'all' | 'app' | 'adapter'>('all');
  const { catalog, alertId } = useCatalog();
  const installed = () => catalog.filter((m) => m.installed).map((m) => m.id);
  const [visit, setVisit] = useState(() => ({ view, before: installed() }));
  if (visit.view !== view) setVisit({ view, before: installed() });
  const label = MARKETPLACE.groups.flatMap((g) => g.items).find((i) => i.key === view)?.label ?? 'Все модули';
  const list = catalog
    .filter(viewFilter(view, visit.before))
    .filter((m) => kind === 'all' || m.kind === kind)
    .filter((m) => matchesQuery(`${m.name} ${m.description}`, query));

  return (
    <Page>
      <PageHeader
        title={label}
        meta={
          <>
            <span>Приложения и подключения к внешним системам</span>
            <span>{catalog.filter((m) => m.installed).length} установлено</span>
          </>
        }
      />
      <Toolbar>
        <div className="w-72">
          <Input
            leadingIcon={<Search />}
            placeholder="Найти модуль"
            aria-label="Найти модуль"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <Chip selected={kind === 'all'} onClick={() => setKind('all')}>
          Все
        </Chip>
        <Chip selected={kind === 'app'} onClick={() => setKind('app')}>
          Приложения
        </Chip>
        <Chip selected={kind === 'adapter'} onClick={() => setKind('adapter')}>
          Подключения
        </Chip>
      </Toolbar>
      <Body className="pt-4">
        {list.length === 0 ? (
          <EmptyState
            icon={<Search />}
            title="Ничего не нашлось"
            description="Попробуйте другое слово — поиск понимает транслит и набор не в той раскладке."
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {list.map((m) => (
              <ModuleCard key={m.id} module={m} alert={m.id === alertId} />
            ))}
          </div>
        )}
      </Body>
    </Page>
  );
}

function ModuleCard({ module: m, alert }: { module: Module; alert: boolean }) {
  const { go } = useWorkspace();
  const { toast } = useToast();
  const connect = useConnect();
  return (
    <article className="flex flex-col gap-3 rounded-lg border border-line bg-card p-5 transition-interactive hover:border-line-strong">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-md border border-line bg-muted text-fg">
          <ModuleIcon id={m.id} className="size-5" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <ObjectLink
            to={at(`/marketplace/m/${m.id}`)}
            className="truncate text-sm font-semibold text-fg hover:underline"
          >
            {m.name}
          </ObjectLink>
          <span className="text-2xs text-fg-subtle">{kindLabel(m)}</span>
        </div>
        <ModuleStatus module={m} alert={alert} />
      </div>
      <p className="line-clamp-3 text-xs leading-5 text-fg-muted">{m.description}</p>
      <div className="mt-auto flex items-center justify-end gap-2 pt-1">
        {m.id === 'infra' && m.active ? (
          <Button size="sm" rightIcon={<ArrowRight />} onClick={() => go(at('/apps/infra'))}>
            Открыть
          </Button>
        ) : m.installed ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => toast({ title: `${m.name}: настройки откроются в приложении`, tone: 'success' })}
          >
            Настроить
          </Button>
        ) : m.soon ? (
          <Button size="sm" variant="ghost" disabled>
            Скоро
          </Button>
        ) : (
          <Button size="sm" onClick={() => connect(m)}>
            Подключить
          </Button>
        )}
      </div>
    </article>
  );
}

/** Значок модуля — общий для каталога, карточки сервиса на веере и строк главной; неизвестному — коробка. */
export function ModuleIcon({ id, className }: { id: string; className?: string }) {
  const Icon = MODULE_ICONS[id] ?? Package;
  return <Icon className={className} />;
}

/*
  Состояние модуля — один словарь для каталога, веера, карточки модуля и строк главной (R9, R22):
  Работает · Выключен · Сбой (только у включённого) · Скоро (только в каталоге). «Подключается»
  отдельного вида не имеет: это сам приход пластины на веере (R32).
*/
export function ModuleStatus({ module: m, alert = false }: { module: Module; alert?: boolean }) {
  if (m.installed && m.active && alert)
    return (
      <Badge variant="urgent" solid>
        Сбой
      </Badge>
    );
  if (m.installed && m.active)
    return (
      <Badge variant="success" solid>
        Работает
      </Badge>
    );
  if (m.installed)
    return (
      <Badge variant="neutral" solid>
        Выключен
      </Badge>
    );
  if (m.soon) return <Badge variant="neutral">Скоро</Badge>;
  return null;
}

/** Карточка модуля — объект, открывается своей вкладкой. */
export function ModuleScreen() {
  const { id = '' } = useParams();
  const { go } = useWorkspace();
  const { catalog, alertId } = useCatalog();
  const connect = useConnect();
  const m = catalog.find((x) => x.id === id);
  if (!m)
    return (
      <ObjectNotFound
        title="Модуль не найден"
        description="Возможно, его убрали из каталога."
        list={{ label: 'В каталог', path: at('/marketplace') }}
        find="Найти модуль · Ctrl+K"
      />
    );
  return (
    <Page>
      <PageHeader
        eyebrow={<Badge variant="neutral">{kindLabel(m)}</Badge>}
        title={m.name}
        status={<ModuleStatus module={m} alert={m.id === alertId} />}
        meta={
          <>
            <span>Владелец — Анна</span>
            <span>Версия 1.4</span>
          </>
        }
        actions={
          m.id === 'infra' && m.active ? (
            <Button rightIcon={<ArrowRight />} onClick={() => go(at('/apps/infra'))}>
              Открыть приложение
            </Button>
          ) : !m.installed && !m.soon ? (
            <Button onClick={() => connect(m)}>Подключить</Button>
          ) : undefined
        }
      />
      <Body>
        <div className="grid gap-4 lg:grid-cols-3">
          <Panel title="О модуле" className="lg:col-span-2">
            <div className="flex items-start gap-4">
              <span className="grid size-12 shrink-0 place-items-center rounded-md border border-line bg-muted text-fg">
                <ModuleIcon id={m.id} className="size-6" />
              </span>
              <p className="text-sm leading-6 text-fg-muted">{m.description}</p>
            </div>
          </Panel>
          <Panel title="Роли">
            <ul className="flex flex-col gap-2 text-sm">
              <li className="flex justify-between">
                <span className="text-fg">Наблюдатель</span>
                <span className="text-fg-subtle">L1</span>
              </li>
              <li className="flex justify-between">
                <span className="text-fg">Дежурный</span>
                <span className="text-fg-subtle">L2</span>
              </li>
              <li className="flex justify-between">
                <span className="text-fg">Администратор</span>
                <span className="text-fg-subtle">владелец</span>
              </li>
            </ul>
          </Panel>
        </div>
      </Body>
    </Page>
  );
}
